import { useEffect, useState } from "react";
import { Download, ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));

/**
 * Descarga manual de backup completo — genera un ZIP en el servidor con
 * JSON de todas las colecciones críticas y lo baja al equipo del admin.
 *
 * Uso típico: antes de bulk upload o cambio de configuración crítica.
 * Ver DATA_SECURITY.md sección 6 y DISASTER_RECOVERY.md tier 1 item 2.
 */
export default function BackupDownloadCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/backup/preview");
      setPreview(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el preview del backup",
        { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Fetch as blob so the browser triggers a file download instead of
      // trying to render the ZIP inline.
      const r = await api.get("/admin/backup/download", { responseType: "blob" });

      // Extract filename from Content-Disposition header if present, else
      // build one from the current timestamp.
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `sdm-bi-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.zip`;

      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Backup descargado", {
        description: `${fname} · ${fmt(r.data.size / 1024)} KB`,
      });
    } catch (e) {
      toast.error("Error al descargar backup",
        { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setDownloading(false);
    }
  };

  const total = preview?.total_documents || 0;
  const collections = preview?.collections || {};

  return (
    <div className="surface p-6" data-testid="backup-download-card">
      <div className="flex items-start gap-4 mb-4">
        <div className="kpi-icon-clean" style={{
          width: 44, height: 44, color: "#0ea5e9",
          boxShadow: "inset 0 0 0 1px #0ea5e933",
        }}>
          <ShieldCheck size={22}/>
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-extrabold" style={{ letterSpacing: "-0.01em" }}>
            Descarga de Backup Completo
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Genera un archivo ZIP con JSON de todas las colecciones críticas (leads, ventas,
            inventario, metas, usuarios, audit log). Úsalo antes de operaciones destructivas
            o como respaldo local periódico.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-sm text-gray-400">
          <Loader2 size={14} className="animate-spin mr-2"/> Calculando tamaño del backup…
        </div>
      ) : (
        <>
          {/* Collections preview grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4" data-testid="backup-preview-grid">
            {Object.entries(collections).map(([name, count]) => (
              <div key={name} className="flex items-center justify-between px-3 py-2 rounded-lg"
                style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <span className="text-[11px] font-bold text-gray-600" style={{
                  fontFamily: "monospace", letterSpacing: "-0.02em",
                }}>{name}</span>
                <span className="text-xs font-extrabold text-gray-900 tabular-nums">
                  {count === 0 ? <span className="text-gray-400">—</span> : fmt(count)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Total documentos
              </div>
              <div className="text-2xl font-extrabold text-gray-900 tabular-nums" style={{
                fontFamily: "Manrope, sans-serif",
              }} data-testid="backup-total-count">
                {fmt(total)}
              </div>
            </div>

            <button
              type="button"
              className="btn"
              onClick={handleDownload}
              disabled={downloading || total === 0}
              data-testid="backup-download-btn"
              style={{
                background: total === 0 ? "#e5e7eb" : "#0ea5e9",
                color: total === 0 ? "#9ca3af" : "#ffffff",
                fontWeight: 800,
                padding: "10px 18px",
                borderRadius: 10,
                border: "none",
                cursor: downloading || total === 0 ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                boxShadow: total === 0 ? "none" : "0 4px 12px rgba(14, 165, 233, 0.25)",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
            >
              {downloading ? (
                <><Loader2 size={16} className="animate-spin"/> Generando ZIP…</>
              ) : (
                <><Download size={16}/> Descargar Backup Ahora</>
              )}
            </button>
          </div>

          <div className="flex items-start gap-2 mt-4 p-3 rounded-lg" style={{
            background: "#fef9c3", border: "1px solid #fde047",
          }}>
            <AlertCircle size={14} style={{ color: "#a16207", flexShrink: 0, marginTop: 2 }}/>
            <div className="text-[11px] font-semibold" style={{ color: "#854d0e", lineHeight: 1.5 }}>
              <strong>Guarda el archivo en un lugar seguro</strong> — idealmente cifrado y con
              acceso restringido. El backup contiene datos personales de leads (PII).
              La descarga queda registrada en el audit log.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
